import { useEffect, useState, type FormEvent } from 'react';
import { CREDIT_TEXT_LIMITS, type PanelCreditContractDto } from '@icebreaker/shared';
import {
  Button,
  DateTimePicker,
  Field,
  Input,
  Modal,
  Textarea,
  useFormErrors,
} from '@icebreaker/ui';
import { numericDateOptions } from '@/features/sales/model/sales-view';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { createContract, creditMutationReset, terminateContract } from '../store/credits.slice';
import {
  DialogFooter,
  DialogSub,
  InfoBlock,
  ServerBanner,
  UnknownOutcome,
  failureKind,
  fieldErrorPaths,
  isConflict,
  useReleaseKeyOnSettle,
  useWriteKey,
  useStaffName,
  useTodayInIstanbul,
} from './dialog-parts';
import styles from './organizations.module.css';

/**
 * KURUMSAL SÖZLEŞME TANIMLA (P6 · 3). Başlangıç ve bitiş takvim günüdür;
 * sözleşme bitiş gününün İstanbul gün sonuna kadar geçerlidir. Bitiş
 * başlangıçtan önce olamaz (istemci ve sunucu aynı kuralı uygular).
 */
export function ContractCreateDialog({
  organizationId,
  organizationName,
  onClose,
}: {
  organizationId: string;
  organizationName: string;
  onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const staffName = useStaffName();
  const mutation = useAppSelector((root: RootState) => root.panelCredits.mutations.contractCreate);
  const today = useTodayInIstanbul();
  const [startDay, setStartDay] = useState(today);
  const [endDay, setEndDay] = useState('');
  const [reference, setReference] = useState('');
  const [touched, setTouched] = useState(false);
  const signature = JSON.stringify([startDay, endDay, reference.trim()]);
  // Kapsam kurumdur: aynı gövde, kesin yanıta kadar aynı anahtarla gider (kapat-aç dahil).
  const writeKey = useWriteKey(`contract:${organizationId}`);
  const outcome = failureKind(mutation.phase, mutation.error);

  useEffect(() => {
    dispatch(creditMutationReset('contractCreate'));
    return () => {
      dispatch(creditMutationReset('contractCreate'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (mutation.phase === 'succeeded') onClose();
  }, [mutation.phase, onClose]);
  useReleaseKeyOnSettle(mutation.phase, outcome, writeKey.release);

  const busy = mutation.phase === 'loading';
  const serverPaths = fieldErrorPaths(mutation.error);
  const serverForm = useFormErrors(mutation.error, ['startDay', 'endDay', 'reference']);
  const startError =
    touched && startDay === ''
      ? t.t('admin.credit.contractDialog.startRequired')
      : serverForm.at('startDay');
  const endError =
    touched && endDay === ''
      ? t.t('admin.credit.contractDialog.endRequired')
      : (endDay !== '' && startDay !== '' && endDay < startDay) || serverPaths.has('endDay')
        ? t.t('admin.credit.contractDialog.endBeforeStart')
        : undefined;
  const referenceError =
    (touched && reference.trim() === '') || serverPaths.has('reference')
      ? t.t('admin.credit.grantDialog.referenceRequired')
      : undefined;
  const conflict = isConflict(mutation.error);
  const failed = outcome !== 'none';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTouched(true);
    if (!startDay || !endDay || endDay < startDay || reference.trim() === '') return;
    const idempotencyKey = writeKey.keyFor(signature);
    dispatch(
      createContract.request({
        organizationId,
        body: { startDay, endDay, reference: reference.trim(), idempotencyKey },
      }),
    );
  };

  return (
    <Modal
      open
      title={t.t('admin.credit.contractDialog.title')}
      closeLabel={t.t('common.close')}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <DialogFooter audit={t.t('admin.orgs.auditLine', { name: staffName })}>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t.t('admin.orgs.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="contract-create-form"
            loading={busy}
            loadingLabel={t.t('admin.credit.contractDialog.submitting')}
          >
            {failed ? t.t('admin.orgs.retry') : t.t('admin.credit.contractDialog.submit')}
          </Button>
        </DialogFooter>
      }
    >
      <DialogSub>{organizationName}</DialogSub>
      <form id="contract-create-form" className={styles.form} onSubmit={submit} noValidate>
        <Field
          id="contract-start"
          label={t.t('admin.credit.contractDialog.start')}
          error={startError}
        >
          <DateTimePicker
            id="contract-start"
            mode="date"
            value={startDay}
            today={today}
            invalid={Boolean(startError)}
            disabled={busy}
            required
            onChange={setStartDay}
          />
        </Field>
        <Field id="contract-end" label={t.t('admin.credit.contractDialog.end')} error={endError}>
          <DateTimePicker
            id="contract-end"
            mode="date"
            value={endDay}
            min={startDay || undefined}
            today={today}
            invalid={Boolean(endError)}
            disabled={busy}
            required
            onChange={setEndDay}
          />
        </Field>
        <Field
          id="contract-reference"
          label={t.t('admin.credit.contractDialog.reference')}
          error={referenceError}
        >
          <Input
            id="contract-reference"
            className={styles.mono}
            value={reference}
            maxLength={CREDIT_TEXT_LIMITS.reference}
            disabled={busy}
            invalid={Boolean(referenceError)}
            onChange={(event) => setReference(event.target.value)}
          />
        </Field>
        <InfoBlock tone="info">{t.t('admin.credit.contractDialog.info')}</InfoBlock>
        {conflict ? (
          <ServerBanner>{t.t('admin.credit.conflict')}</ServerBanner>
        ) : outcome === 'unknown' ? (
          <UnknownOutcome reloaded={t.t('admin.write.reloadedContracts')} />
        ) : outcome === 'rejected' ? (
          <ServerBanner>{t.t('admin.credit.contractDialog.serverError')}</ServerBanner>
        ) : null}
      </form>
    </Modal>
  );
}

/**
 * SÖZLEŞMEYİ SONLANDIR (P6 · 3). Gerekçe zorunlu. Sonlandırma anından sonra
 * yayınlar krediye düşer, yayındaki etkinlikler etkilenmez. Sunucuda bu uç
 * yineleme anahtarı istemez: ikinci gönderim "zaten sonlandırılmış" (409) alır
 * ve düğme gönderim boyunca kilitlidir.
 */
export function ContractTerminateDialog({
  organizationId,
  contract,
  onClose,
}: {
  organizationId: string;
  contract: PanelCreditContractDto;
  onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const staffName = useStaffName();
  const mutation = useAppSelector(
    (root: RootState) => root.panelCredits.mutations.contractTerminate,
  );
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    dispatch(creditMutationReset('contractTerminate'));
    return () => {
      dispatch(creditMutationReset('contractTerminate'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (mutation.phase === 'succeeded') onClose();
  }, [mutation.phase, onClose]);

  const busy = mutation.phase === 'loading';
  const serverPaths = fieldErrorPaths(mutation.error);
  const serverForm = useFormErrors(mutation.error, ['reason']);
  const reasonError =
    touched && reason.trim() === ''
      ? t.t('admin.credit.terminateDialog.reasonRequired')
      : serverPaths.has('reason')
        ? (serverForm.at('reason') ?? t.t('admin.credit.terminateDialog.reasonRequired'))
        : undefined;
  // Sonlandırma yineleme anahtarı istemez: ikinci gönderim "zaten sonlandırılmış" (409) alır.
  const outcome = failureKind(mutation.phase, mutation.error);
  const failed = outcome !== 'none';
  const dateOptions = numericDateOptions(t.locale);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTouched(true);
    if (reason.trim() === '') return;
    dispatch(
      terminateContract.request({
        organizationId,
        contractId: contract.id,
        body: { reason: reason.trim() },
      }),
    );
  };

  return (
    <Modal
      open
      title={t.t('admin.credit.terminateDialog.title')}
      closeLabel={t.t('common.close')}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <DialogFooter audit={t.t('admin.orgs.auditLine', { name: staffName })}>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t.t('admin.orgs.cancel')}
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="contract-terminate-form"
            loading={busy}
            loadingLabel={t.t('admin.credit.terminateDialog.submitting')}
          >
            {failed ? t.t('admin.orgs.retry') : t.t('admin.credit.terminateDialog.submit')}
          </Button>
        </DialogFooter>
      }
    >
      <DialogSub>
        {t.t('admin.credit.terminateDialog.sub', {
          reference: contract.reference ?? '—',
          start: t.formatDate(contract.startsAt, dateOptions),
          end: t.formatDate(contract.endsAt, dateOptions),
        })}
      </DialogSub>
      <form id="contract-terminate-form" className={styles.form} onSubmit={submit} noValidate>
        <Field
          id="terminate-reason"
          label={t.t('admin.credit.terminateDialog.reason')}
          error={reasonError}
        >
          <Textarea
            id="terminate-reason"
            rows={3}
            value={reason}
            maxLength={CREDIT_TEXT_LIMITS.reason}
            disabled={busy}
            invalid={Boolean(reasonError)}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        <InfoBlock tone="warn">{t.t('admin.credit.terminateDialog.info')}</InfoBlock>
        {outcome === 'unknown' ? (
          <UnknownOutcome reloaded={t.t('admin.write.reloadedTermination')} />
        ) : outcome === 'rejected' ? (
          <ServerBanner>
            {isConflict(mutation.error)
              ? t.t('admin.credit.terminateDialog.alreadyTerminated')
              : t.t('admin.credit.terminateDialog.serverError')}
          </ServerBanner>
        ) : null}
      </form>
    </Modal>
  );
}
